/**
 * Multilingual spoken-alarm phrase dictionary — 6 languages × 4 levels × 9 gases.
 *
 * The 6-language set matches the TBM-kiosk / 협약 standard:
 *   ko (한국어) · en (English) · zh (中文 简体) · vi (Tiếng Việt) · th (ไทย) · id (Bahasa Indonesia)
 *
 * The 9 canonical gases mirror the backend field taxonomy
 * (`core/management/commands/seed_field.py :: NODE_GASES`):
 *   O2 · CO · H2S · CH4 · CO2 · NH3 · SO2 · NO2 · VOC
 * plus two commonly-reported extras (LEL combustible, SMOKE) for completeness.
 *
 * `buildAlertPhrase()` composes a full spoken sentence from
 * (level + gas + optional site) — e.g.
 *   ko  «심각 경보. 3동 황화수소 누출. 즉시 가까운 비상구로 대피하십시오.»
 *   en  «Critical alert. H2S leak at Bldg 3. Evacuate to the nearest emergency exit immediately.»
 * Grammar/word-order differs per language, so each language owns a small
 * `compose()` rather than a shared placeholder template.
 *
 * These are the strings the Web Speech API (and the edge-tts server fallback)
 * read aloud. `SPEECH_LOCALE` maps each app language to its BCP-47 speech tag.
 */

export const ALERT_LANGUAGES = ["ko", "en", "zh", "vi", "th", "id"] as const;
export type AlertLang = (typeof ALERT_LANGUAGES)[number];

export type Level = 0 | 1 | 2 | 3;

/** App language → BCP-47 tag for speechSynthesis + edge-tts voice lookup. */
export const SPEECH_LOCALE: Record<AlertLang, string> = {
  ko: "ko-KR",
  en: "en-US",
  zh: "zh-CN",
  vi: "vi-VN",
  th: "th-TH",
  id: "id-ID",
};

/** Native-script label for each language (used by the manual selector). */
export const LANG_LABEL: Record<AlertLang, string> = {
  ko: "한국어",
  en: "English",
  zh: "中文",
  vi: "Tiếng Việt",
  th: "ไทย",
  id: "Bahasa",
};

/** Short level word (정상/주의/경계/심각) per language, indexed by level 0..3. */
export const LEVEL_WORD: Record<AlertLang, [string, string, string, string]> = {
  ko: ["정상", "주의", "경계", "심각"],
  en: ["Normal", "Caution", "Warning", "Critical"],
  zh: ["正常", "注意", "警戒", "严重"],
  vi: ["Bình thường", "Chú ý", "Cảnh giác", "Nghiêm trọng"],
  th: ["ปกติ", "ระวัง", "เตือนภัย", "วิกฤต"],
  id: ["Normal", "Perhatian", "Waspada", "Kritis"],
};

/**
 * Gas display names per language, keyed by canonical uppercase code.
 * `_multi` is the fallback when the gas is unknown / a composite alert.
 */
const GAS_NAME: Record<AlertLang, Record<string, string>> = {
  ko: {
    O2: "산소", CO: "일산화탄소", H2S: "황화수소", CH4: "메탄", CO2: "이산화탄소",
    NH3: "암모니아", SO2: "이산화황", NO2: "이산화질소", VOC: "휘발성 유기화합물",
    LEL: "가연성 가스", SMOKE: "연기", _multi: "복합 가스",
  },
  en: {
    O2: "oxygen", CO: "carbon monoxide", H2S: "hydrogen sulfide", CH4: "methane",
    CO2: "carbon dioxide", NH3: "ammonia", SO2: "sulfur dioxide",
    NO2: "nitrogen dioxide", VOC: "volatile organic compounds",
    LEL: "combustible gas", SMOKE: "smoke", _multi: "multiple gases",
  },
  zh: {
    O2: "氧气", CO: "一氧化碳", H2S: "硫化氢", CH4: "甲烷", CO2: "二氧化碳",
    NH3: "氨气", SO2: "二氧化硫", NO2: "二氧化氮", VOC: "挥发性有机物",
    LEL: "可燃气体", SMOKE: "烟雾", _multi: "复合气体",
  },
  vi: {
    O2: "ôxy", CO: "cacbon monoxit", H2S: "hydro sunfua", CH4: "mêtan",
    CO2: "cacbon điôxit", NH3: "amoniac", SO2: "lưu huỳnh điôxit",
    NO2: "nitơ điôxit", VOC: "hợp chất hữu cơ dễ bay hơi",
    LEL: "khí dễ cháy", SMOKE: "khói", _multi: "nhiều loại khí",
  },
  th: {
    O2: "ออกซิเจน", CO: "คาร์บอนมอนอกไซด์", H2S: "ไฮโดรเจนซัลไฟด์", CH4: "มีเทน",
    CO2: "คาร์บอนไดออกไซด์", NH3: "แอมโมเนีย", SO2: "ซัลเฟอร์ไดออกไซด์",
    NO2: "ไนโตรเจนไดออกไซด์", VOC: "สารอินทรีย์ระเหย",
    LEL: "ก๊าซไวไฟ", SMOKE: "ควัน", _multi: "ก๊าซหลายชนิด",
  },
  id: {
    O2: "oksigen", CO: "karbon monoksida", H2S: "hidrogen sulfida", CH4: "metana",
    CO2: "karbon dioksida", NH3: "amonia", SO2: "sulfur dioksida",
    NO2: "nitrogen dioksida", VOC: "senyawa organik volatil",
    LEL: "gas mudah terbakar", SMOKE: "asap", _multi: "beberapa gas",
  },
};

/** Human gas name in a language, falling back to the raw code / composite term. */
export function gasDisplayName(lang: AlertLang, gas?: string | null): string {
  const table = GAS_NAME[lang] ?? GAS_NAME.ko;
  if (!gas) return table._multi;
  const code = gas.trim().toUpperCase();
  return table[code] ?? gas; // unknown code → speak it verbatim
}

/** Short level label ("심각" / "Critical" / …). */
export function levelLabel(lang: AlertLang, level: Level): string {
  return (LEVEL_WORD[lang] ?? LEVEL_WORD.ko)[level] ?? "";
}

// ── Per-language sentence composition ────────────────────────────────────────
// Each returns a complete utterance for (level, gas-name, optional site name).
type Compose = (level: Level, gas: string, site?: string) => string;

const COMPOSE: Record<AlertLang, Compose> = {
  ko: (level, gas, site) => {
    const loc = site ? `${site} ` : "";
    switch (level) {
      case 3: return `심각 경보. ${loc}${gas} 누출. 즉시 가까운 비상구로 대피하십시오.`;
      case 2: return `경계 경보. ${loc}${gas} 감지. 작업을 멈추고 환기하십시오.`;
      case 1: return `주의. ${loc}${gas} 농도가 상승했습니다. 주변을 확인하십시오.`;
      default: return `정상. ${loc}${gas} 농도는 정상 범위입니다.`;
    }
  },
  en: (level, gas, site) => {
    const loc = site ? ` at ${site}` : "";
    switch (level) {
      case 3: return `Critical alert. ${gas} leak${loc}. Evacuate to the nearest emergency exit immediately.`;
      case 2: return `Warning. ${gas} detected${loc}. Stop work and ventilate the area.`;
      case 1: return `Caution. Rising ${gas} level${loc}. Check your surroundings.`;
      default: return `Normal. ${gas} level${loc} is within the normal range.`;
    }
  },
  zh: (level, gas, site) => {
    const loc = site ? `在${site}，` : "";
    switch (level) {
      case 3: return `${loc}严重警报。检测到${gas}泄漏。请立即前往最近的紧急出口疏散。`;
      case 2: return `${loc}警戒。检测到${gas}。请停止作业并通风。`;
      case 1: return `${loc}注意。${gas}浓度上升。请检查周围环境。`;
      default: return `${loc}正常。${gas}浓度处于正常范围。`;
    }
  },
  vi: (level, gas, site) => {
    const loc = site ? ` tại ${site}` : "";
    switch (level) {
      case 3: return `Cảnh báo nghiêm trọng. Rò rỉ ${gas}${loc}. Sơ tán ngay đến lối thoát hiểm gần nhất.`;
      case 2: return `Cảnh giác. Phát hiện ${gas}${loc}. Ngừng công việc và thông gió khu vực.`;
      case 1: return `Chú ý. Nồng độ ${gas} đang tăng${loc}. Kiểm tra xung quanh.`;
      default: return `Bình thường. Nồng độ ${gas}${loc} trong ngưỡng an toàn.`;
    }
  },
  th: (level, gas, site) => {
    const loc = site ? `ที่ ${site} ` : "";
    switch (level) {
      case 3: return `แจ้งเตือนขั้นวิกฤต ${loc}พบการรั่วไหลของ${gas} อพยพไปยังทางออกฉุกเฉินที่ใกล้ที่สุดทันที`;
      case 2: return `เตือนภัย ${loc}ตรวจพบ${gas} หยุดการทำงานและระบายอากาศ`;
      case 1: return `ระวัง ${loc}ระดับ${gas}กำลังสูงขึ้น ตรวจสอบบริเวณโดยรอบ`;
      default: return `ปกติ ${loc}ระดับ${gas}อยู่ในเกณฑ์ปกติ`;
    }
  },
  id: (level, gas, site) => {
    const loc = site ? ` di ${site}` : "";
    switch (level) {
      case 3: return `Peringatan kritis. Kebocoran ${gas}${loc}. Segera evakuasi ke pintu darurat terdekat.`;
      case 2: return `Waspada. ${gas} terdeteksi${loc}. Hentikan pekerjaan dan lakukan ventilasi.`;
      case 1: return `Perhatian. Kadar ${gas} meningkat${loc}. Periksa sekitar Anda.`;
      default: return `Normal. Kadar ${gas}${loc} dalam rentang aman.`;
    }
  },
};

export interface AlertPhraseInput {
  lang: AlertLang;
  level: Level;
  /** Canonical gas code (e.g. "H2S") or a name; empty → composite phrasing. */
  gas?: string | null | undefined;
  /** Optional site/location clause (already localised name). */
  site?: string | null | undefined;
}

/**
 * Build the full spoken sentence for an alert in the given language.
 * Safe for any input: unknown language → ko, unknown gas → spoken verbatim.
 */
export function buildAlertPhrase({ lang, level, gas, site }: AlertPhraseInput): string {
  const l: AlertLang = ALERT_LANGUAGES.includes(lang) ? lang : "ko";
  const compose = COMPOSE[l];
  const gasName = gasDisplayName(l, gas);
  return compose(level, gasName, site ?? undefined);
}

/** Normalise any i18n language string ("ko-KR", "zh-CN", "zh") to an AlertLang. */
export function toAlertLang(lng?: string | null): AlertLang {
  const base = (lng ?? "ko").toLowerCase().split("-")[0];
  return (ALERT_LANGUAGES as readonly string[]).includes(base) ? (base as AlertLang) : "ko";
}
